import React, { useState } from "react";
import { buildImageUrl, buildSrcSet } from "../../lib/imageSource";

/**
 * Every photograph on the site goes through this component.
 *
 * It adds responsive `srcset` and edge-side AVIF conversion via Netlify Image
 * CDN (see `src/lib/imageSource.js`), which is the difference between a phone
 * downloading a 2148px JPEG and a 480px AVIF.
 *
 * If a transformed URL fails to load for any reason — the CDN is unavailable,
 * the bucket was not allow-listed, the site moved off Netlify — the component
 * falls back to the original URL once and renders exactly what it would have
 * rendered before. A misconfiguration therefore costs performance, never a
 * broken image.
 *
 * @param {Object} props
 * @param {string} props.src          Original image URL.
 * @param {string} props.alt          Alt text. Pass "" for decorative images.
 * @param {string} [props.sizes]      Responsive sizes hint.
 * @param {number} [props.sourceWidth] Intrinsic width, so srcset never upscales.
 * @param {"lazy"|"eager"} [props.loading="lazy"]
 * @param {string} [props.className]
 * @param {boolean} [props.priority=false] High fetch priority for an LCP image.
 * @param {React.Ref<HTMLImageElement>} ref Forwarded, so a caller can observe
 *   the element — `LiveCover` watches it to decide when a project's preview
 *   loop is close enough to the viewport to be worth downloading.
 * @returns {JSX.Element}
 */
const SmartImage = React.forwardRef(function SmartImage(
  {
    src,
    alt,
    sizes,
    sourceWidth,
    loading = "lazy",
    className,
    priority = false,
    ...remainingProps
  },
  ref
) {
  const [hasFallenBack, setHasFallenBack] = useState(false);

  const transformedSrc = hasFallenBack ? src : buildImageUrl(src);
  const transformedSrcSet = hasFallenBack
    ? undefined
    : buildSrcSet(src, { maxWidth: sourceWidth });

  return (
    <img
      ref={ref}
      className={className}
      src={transformedSrc}
      srcSet={transformedSrcSet}
      sizes={transformedSrcSet ? sizes : undefined}
      alt={alt}
      loading={priority ? "eager" : loading}
      decoding="async"
      fetchpriority={priority ? "high" : undefined}
      onError={() => {
        // Retry once with the untouched original before giving up.
        if (!hasFallenBack) setHasFallenBack(true);
      }}
      {...remainingProps}
    />
  );
});

export default SmartImage;
